import { Component } from '@grafikr/components';
import fastdom from 'fastdom';
import { debounceTime, fromEvent } from 'rxjs';

import loadScript from 'utils/load-script';
import { NonNullish } from 'utils/nullish-check';

interface DotLottie extends Element {
  load: (url: string) => void;
}

export default Component(async (node) => {
  const parent = NonNullish(node.parentElement);

  const STATIC_LOGO = node.querySelector<SVGElement>('[data-static-logo]');
  const STICKY_LOGO = node.querySelector<SVGElement>('[data-sticky-logo]');
  const LOTTIE_FILE = STICKY_LOGO?.querySelector<DotLottie>('#lottie-logo');
  let logoHasAnimated = false;

  if (LOTTIE_FILE) {
    await loadScript(
      'https://unpkg.com/@dotlottie/player-component@latest/dist/dotlottie-player.mjs',
      { type: 'module' },
    );
  }

  const HIDDEN_CLASSES = ['motion-safe:-translate-y-full', 'motion-reduce:opacity-0'];
  const SCROLLED_CLASSES = ['bg-white', 'header--scrolled'];

  let dependencyLock = false;

  const cartCountElement = node.querySelector<HTMLSpanElement>('[data-cart-count]');

  document.addEventListener('header-dependency:lock', () => {
    dependencyLock = true;
  });

  document.addEventListener('header-dependency:unlock', () => {
    dependencyLock = false;
  });

  let lastScrollPosition = window.scrollY;
  let job: () => void;

  parent.classList.add('duration-300');

  const updateCartCount = (count: number) => {
    if (!cartCountElement) {
      return;
    }

    NonNullish(cartCountElement.parentElement).hidden = count < 1;

    cartCountElement.textContent = count.toString();
  };

  const initLottieLogo = async () => {
    if (!LOTTIE_FILE || logoHasAnimated) return;

    LOTTIE_FILE.load('https://lottie.host/801320f5-5ef2-4929-990b-e0f0155ae428/2PKBw4xniI.json');
    logoHasAnimated = true;
  };

  const updateLogo = async (isSticky: boolean) => {
    if (!STATIC_LOGO || !STICKY_LOGO) {
      return;
    }

    if (isSticky) {
      STATIC_LOGO.classList.add('hidden');
      STICKY_LOGO.classList.remove('hidden');
      await initLottieLogo();
    } else {
      STATIC_LOGO.classList.remove('hidden');
      STICKY_LOGO.classList.add('hidden');
    }
  };

  const scrollEvent = () => {
    if (job) {
      fastdom.clear(job);
    }

    job = fastdom.mutate(async () => {
      if (window.scrollY <= 100 || dependencyLock) {
        parent.classList.remove(...HIDDEN_CLASSES, ...SCROLLED_CLASSES);
        await updateLogo(false);
      } else if (window.scrollY > lastScrollPosition) {
        parent.classList.add(...HIDDEN_CLASSES);
      } else {
        parent.classList.remove(...HIDDEN_CLASSES);
        parent.classList.add(...SCROLLED_CLASSES);
        await updateLogo(true);
      }

      lastScrollPosition = window.scrollY;
    });
  };

  const setHeaderHeight = () =>
    document.body.style.setProperty('--header-height', `${node.clientHeight}px`);

  window.addEventListener('scroll', scrollEvent, {
    passive: true,
  });

  setHeaderHeight();
  fromEvent(window, 'resize').pipe(debounceTime(250)).subscribe(setHeaderHeight);

  document.addEventListener('cart-count:updated', (event) => {
    updateCartCount((event as CustomEvent<number>).detail);
  });
});
